<!--
 * @Description: 智能助手（悬浮球 + 对话框，当前为模拟回复演示版）
-->
<template>
  <div class="ai-assistant">
    <!-- 对话框面板 -->
    <transition name="ai-panel">
      <div v-show="visible" class="ai-panel">
        <!-- 头部 -->
        <div class="ai-panel-header">
          <div class="ai-panel-title">
            <el-icon><ChatDotRound /></el-icon>
            <span>人影智能助手</span>
          </div>
          <el-button
            class="ai-close-btn"
            :icon="Close"
            circle
            text
            @click="visible = false"
          />
        </div>

        <!-- 消息列表 -->
        <div ref="messagesRef" class="ai-messages">
          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="ai-message"
            :class="msg.role"
          >
            <div class="ai-avatar" :class="msg.role">
              <el-icon v-if="msg.role === 'ai'"><ChatDotRound /></el-icon>
              <el-icon v-else><User /></el-icon>
            </div>
            <div class="ai-bubble">{{ msg.content }}</div>
          </div>

          <!-- 打字中 -->
          <div v-if="isTyping" class="ai-message ai">
            <div class="ai-avatar ai">
              <el-icon><ChatDotRound /></el-icon>
            </div>
            <div class="ai-bubble typing">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
        </div>

        <!-- 快捷功能 -->
        <div class="ai-quick-actions">
          <div
            v-for="action in quickActions"
            :key="action.id"
            class="quick-action-btn"
            :class="{ disabled: isTyping || streaming }"
            @click="sendQuickAction(action)"
          >
            <el-icon><component :is="action.icon" /></el-icon>
            <span>{{ action.label }}</span>
          </div>
        </div>

        <!-- 输入区 -->
        <div class="ai-input-area">
          <el-input
            v-model="inputText"
            placeholder="请输入您的问题..."
            :disabled="isTyping || streaming"
            clearable
            @keyup.enter="sendMessage"
          />
          <el-button
            type="primary"
            class="ai-send-btn"
            :icon="Promotion"
            :disabled="isTyping || streaming || !inputText.trim()"
            @click="sendMessage"
          />
        </div>
      </div>
    </transition>

    <!-- 悬浮球 -->
    <div class="ai-fab" title="智能助手" @click="togglePanel">
      <el-icon :size="26"><ChatDotRound /></el-icon>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, nextTick, watch } from 'vue';
import {
  ChatDotRound,
  Close,
  User,
  Promotion,
  TrendCharts,
  Document,
  CircleCheck,
  DataAnalysis
} from '@element-plus/icons-vue';

const visible = ref(false);
const inputText = ref('');
const isTyping = ref(false);
const streaming = ref(false);
const messagesRef = ref(null);
const messages = ref([]);

// 快捷功能及模拟回复
const quickActions = [
  {
    id: 'radar',
    label: '雷达外推',
    icon: TrendCharts,
    reply:
      '雷达外推分析完成：\n当前识别到对流单体位于测站西南方向约 25km 处，中心最大反射率 55dBZ，移向东北（方位 65°），移速约 35km/h。\n预计未来 30 分钟内将影响盘州北部乡镇，回波强度呈增强趋势，建议提前做好作业准备。'
  },
  {
    id: 'plan',
    label: '人影作业方案',
    icon: Document,
    reply:
      '人影作业方案已生成：\n1. 作业目的：抑制冰雹增长、减轻雹灾损失\n2. 推荐作业点：城关炮点、两河炮点\n3. 建议用弹量：24 发\n4. 作业时段：未来 30 分钟内\n5. 射击方式：扇形多点射击，仰角 45°~65°\n请结合空域批复情况执行。'
  },
  {
    id: 'feasibility',
    label: '作业可行性评估',
    icon: CircleCheck,
    reply:
      '作业可行性评估结果：\n空域条件：已提交空域申请，当前无航班冲突；\n云层条件：云顶高度约 12km，0℃层高度 4.5km，过冷水含量充足；\n综合评估结论：适宜开展防雹作业。'
  },
  {
    id: 'effect',
    label: '作业效果评估',
    icon: DataAnalysis,
    reply:
      '作业效果评估报告：\n本次作业共发射炮弹 24 发。作业后 30 分钟内，目标区域雷达回波强度由 55dBZ 降至 42dBZ，回波顶高下降约 2km，未再出现新增冰雹观测记录。\n评估结论：本次作业防雹效果显著，综合等级为良好。'
  }
];

// 默认欢迎语
const welcomeMessage =
  '您好！我是人影智能助手，可以为您提供雷达外推、人影作业方案、作业可行性评估、作业效果评估等服务。\n您可以直接提问，也可以点击下方的功能按钮开始使用。';

const togglePanel = () => {
  visible.value = !visible.value;
  if (visible.value && messages.value.length === 0) {
    // 首次打开，推入欢迎语
    isTyping.value = true;
    setTimeout(() => {
      isTyping.value = false;
      messages.value.push({ role: 'ai', content: welcomeMessage });
      scrollToBottom();
    }, 600);
  }
};

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesRef.value) {
      messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
    }
  });
};

watch(() => messages.value.length, scrollToBottom);

// 发送输入框内容
const sendMessage = () => {
  const text = inputText.value.trim();
  if (!text || isTyping.value || streaming.value) return;
  inputText.value = '';
  messages.value.push({ role: 'user', content: text });
  fakeReply(
    `已收到您的问题：「${text}」。\n当前为演示模式，稍后将接入真实智能体服务，为您生成专业的分析结果，敬请期待。`
  );
};

// 点击快捷功能
const sendQuickAction = (action) => {
  if (isTyping.value || streaming.value) return;
  messages.value.push({ role: 'user', content: action.label });
  fakeReply(action.reply);
};

// 模拟回复：先显示"打字中"，再逐字输出
const fakeReply = (content) => {
  isTyping.value = true;
  setTimeout(() => {
    isTyping.value = false;
    streaming.value = true;

    const msg = reactive({ role: 'ai', content: '' });
    messages.value.push(msg);

    let i = 0;
    const timer = setInterval(() => {
      i += 2;
      msg.content = content.slice(0, i);
      scrollToBottom();
      if (i >= content.length) {
        clearInterval(timer);
        streaming.value = false;
      }
    }, 30);
  }, 800);
};
</script>

<style scoped>
/* ===================== 悬浮球 ===================== */
.ai-fab {
  position: fixed;
  right: 32px;
  bottom: 32px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  z-index: 3000;
}
.ai-fab:hover {
  transform: scale(1.08);
  box-shadow: 0 8px 26px rgba(37, 99, 235, 0.5);
}

/* ===================== 对话框面板 ===================== */
.ai-panel {
  position: fixed;
  right: 32px;
  bottom: 104px;
  width: 680px;
  max-width: calc(100vw - 40px);
  height: 820px;
  max-height: calc(100vh - 150px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 48px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 3000;
}

/* 头部 */
.ai-panel-header {
  position: relative;
  flex-shrink: 0;
  padding: 16px 20px;
  border-bottom: 1px solid #eef1f6;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
}
.ai-panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}
.ai-panel-title .el-icon {
  color: #2563eb;
  font-size: 20px;
}
.ai-close-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
}

/* 消息列表 */
.ai-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f8fafc;
}
.ai-message {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.ai-message.user {
  flex-direction: row-reverse;
}
.ai-avatar {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}
.ai-avatar.ai {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
}
.ai-avatar.user {
  background: #94a3b8;
}
.ai-bubble {
  max-width: 78%;
  padding: 13px 18px;
  border-radius: 12px;
  font-size: 15px;
  line-height: 1.75;
  white-space: pre-line;
  word-break: break-word;
}
.ai-message.ai .ai-bubble {
  background: #fff;
  color: #1e293b;
  border: 1px solid #e8edf4;
  border-top-left-radius: 4px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}
.ai-message.user .ai-bubble {
  background: #2563eb;
  color: #fff;
  border-top-right-radius: 4px;
}

/* 打字动画 */
.ai-bubble.typing {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 14px 18px;
}
.ai-bubble.typing .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #94a3b8;
  animation: ai-dot-bounce 1.2s infinite ease-in-out;
}
.ai-bubble.typing .dot:nth-child(2) {
  animation-delay: 0.2s;
}
.ai-bubble.typing .dot:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes ai-dot-bounce {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-5px);
    opacity: 1;
  }
}

/* 快捷功能 */
.ai-quick-actions {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #eef1f6;
  background: #fff;
}
.quick-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 12px 8px;
  border: 1px solid #dbe3ee;
  border-radius: 10px;
  font-size: 14px;
  color: #334155;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}
.quick-action-btn .el-icon {
  color: #2563eb;
  font-size: 16px;
}
.quick-action-btn:hover {
  background: #eff6ff;
  border-color: #93c5fd;
  color: #1d4ed8;
}
.quick-action-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.quick-action-btn.disabled:hover {
  background: #f8fafc;
  border-color: #dbe3ee;
  color: #334155;
}

/* 输入区 */
.ai-input-area {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px 18px;
  background: #fff;
}
.ai-input-area :deep(.el-input__wrapper) {
  border-radius: 10px;
  padding: 4px 14px;
}
.ai-send-btn {
  flex-shrink: 0;
  border-radius: 10px;
  height: 40px;
  width: 46px;
}

/* 滚动条 */
.ai-messages::-webkit-scrollbar {
  width: 6px;
}
.ai-messages::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}
.ai-messages::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 面板开关过渡 */
.ai-panel-enter-active,
.ai-panel-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.ai-panel-enter-from,
.ai-panel-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.97);
}
</style>
